import type { Database } from '@sixam/shared/database.types';

export type IntegrationProvider = Database['public']['Enums']['integration_provider'];

export type IntegrationOwnerType = 'user' | 'tenant';

export type IntegrationStatus = {
  provider: IntegrationProvider;
  connected: boolean;
  available: boolean;
  label: string | null;
  message: string | null;
  username: string | null;
  displayName: string | null;
  biography: string | null;
  profilePictureUrl: string | null;
  website: string | null;
  followersCount: number | null;
  mediaCount: number | null;
  externalUserId: string | null;
  tokenExpiresAt: string | null;
  connectedAt: string | null;
  scopes: string[];
};

export type InstagramIntegrationStatus = Omit<
  IntegrationStatus,
  'provider' | 'available' | 'label' | 'message'
>;

export type IntegrationProviderConfig = {
  provider: IntegrationProvider;
  title: string;
  shortTitle: string;
  category: 'calendar' | 'social' | 'fitness';
  supportsUser: boolean;
  supportsTenant: boolean;
  supportsOAuth: boolean;
  supportsEventAction: boolean;
  unavailableMessage?: string;
};

export const INTEGRATION_PROVIDERS = [
  {
    provider: 'google_calendar',
    title: 'Google Calendar',
    shortTitle: 'Google',
    category: 'calendar',
    supportsUser: true,
    supportsTenant: true,
    supportsOAuth: true,
    supportsEventAction: true,
  },
  {
    provider: 'apple_calendar',
    title: 'Apple Calendar',
    shortTitle: 'Apple',
    category: 'calendar',
    supportsUser: true,
    supportsTenant: false,
    supportsOAuth: false,
    supportsEventAction: true,
  },
  {
    provider: 'instagram',
    title: 'Instagram',
    shortTitle: 'Instagram',
    category: 'social',
    supportsUser: true,
    supportsTenant: true,
    supportsOAuth: true,
    supportsEventAction: false,
  },
  {
    provider: 'strava',
    title: 'Strava',
    shortTitle: 'Strava',
    category: 'fitness',
    supportsUser: true,
    supportsTenant: false,
    supportsOAuth: true,
    supportsEventAction: false,
  },
  {
    provider: 'garmin',
    title: 'Garmin',
    shortTitle: 'Garmin',
    category: 'fitness',
    supportsUser: true,
    supportsTenant: true,
    supportsOAuth: true,
    supportsEventAction: false,
    unavailableMessage: 'Garmin API access is pending developer approval.',
  },
] as const satisfies readonly IntegrationProviderConfig[];

export function getIntegrationProviderConfig(
  provider: IntegrationProvider
): IntegrationProviderConfig {
  return (
    INTEGRATION_PROVIDERS.find((item) => item.provider === provider) ?? INTEGRATION_PROVIDERS[0]
  );
}
